import React from "react";
import {
  LayoutDashboard,
  Building2,
  Menu,
  X,
  ChevronLeft,
  Settings,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface SuperAdminSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobile: boolean;
}

const SuperAdminSidebar: React.FC<SuperAdminSidebarProps> = ({
  isCollapsed,
  onToggle,
  isMobile,
}) => {
  const location = useLocation();
  const currentPage = location.pathname.split("/")[1];
  const navigate = useNavigate();
  const { t } = useTranslation("navigation");

  const handleNavigation = (path: string) => {
    navigate(path);

    if (isMobile && !isCollapsed) {
      onToggle();
    }
  };

  const handleOverlayClick = () => {
    if (isMobile && !isCollapsed) {
      onToggle();
    }
  };

  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: t("items.dashboard") },
    { path: "/companies", icon: Building2, label: t("items.companies") },
    { path: "/profile", icon: Settings, label: t("items.profile") },
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && !isCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:relative top-0 left-0 h-full bg-[#0B0F0E] border-r border-white/8 z-50
          transition-all duration-300 ease-in-out
          ${
            isMobile
              ? isCollapsed
                ? "-translate-x-full"
                : "translate-x-0 w-64"
              : isCollapsed
                ? "w-16"
                : "w-64"
          }
        `}
        role="navigation"
        aria-label="SuperAdmin navigation"
      >
        {/* Header */}
        {(!isCollapsed || isMobile) && (
          <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Brand SVG Mark */}
              <svg width="28" height="28" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="22" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5"/>
                <rect x="27" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6"/>
                <rect x="32" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7"/>
                <rect x="38" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85"/>
                <rect x="44" y="18" width="4" height="64" fill="#E89D5C"/>
                <rect x="51" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85"/>
                <rect x="57" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7"/>
                <rect x="63" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6"/>
                <rect x="68" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5"/>
              </svg>
              <div>
                <div className="font-medium tracking-tight text-[#E89D5C]">
                  LiveCargo<span className="text-[#C97A3C]">.</span>
                </div>
                <div className="text-xs text-[#8A9590]">{t("sidebar.adminPortal")}</div>
              </div>
            </div>

            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-white/8 transition-colors"
              aria-label={isCollapsed ? t("sidebar.expandSidebar") : t("sidebar.collapseSidebar")}
            >
              {isMobile ? (
                <X className="h-5 w-5 text-[#8A9590]" />
              ) : (
                <ChevronLeft className="h-5 w-5 text-[#8A9590]" />
              )}
            </button>
          </div>
        )}

        {/* Collapsed Header */}
        {isCollapsed && !isMobile && (
          <div className="py-4 px-2 border-b border-white/8 flex flex-col items-center space-y-2">
            {/* Brand Mark */}
            <svg width="28" height="28" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect x="22" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5"/>
              <rect x="27" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6"/>
              <rect x="32" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7"/>
              <rect x="38" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85"/>
              <rect x="44" y="18" width="4" height="64" fill="#E89D5C"/>
              <rect x="51" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85"/>
              <rect x="57" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7"/>
              <rect x="63" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6"/>
              <rect x="68" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5"/>
            </svg>
            <button
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-white/8 transition-colors"
              aria-label={t("sidebar.expandSidebar")}
            >
              <Menu className="h-5 w-5 text-[#8A9590]" />
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav
          className={`${isCollapsed && !isMobile ? "p-2" : "p-4"}`}
          role="menubar"
        >
          <ul className="space-y-1" role="none">
            {navItems.map((item) => (
              <li key={item.path} role="none">
                <button
                  onClick={() => handleNavigation(item.path)}
                  className={`
                    flex items-center rounded-lg font-normal w-full text-left
                    transition-all duration-200 group relative
                    ${
                      isCollapsed && !isMobile
                        ? "justify-center p-3"
                        : "space-x-3 px-3 py-2"
                    }
                    ${
                      currentPage === item.path.substring(1)
                        ? "text-[#E89D5C] bg-white/8"
                        : "text-[#8A9590] hover:text-[#E89D5C] hover:bg-white/5"
                    }
                  `}
                  role="menuitem"
                  aria-current={
                    currentPage === item.path.substring(1) ? "page" : undefined
                  }
                >
                  <item.icon className="h-5 w-5 shrink-0" />

                  {(!isCollapsed || isMobile) && (
                    <span className="whitespace-nowrap">{item.label}</span>
                  )}

                  {/* Tooltip for collapsed state */}
                  {isCollapsed && !isMobile && (
                    <div
                      className="
                      absolute left-full ml-2 px-2 py-1 bg-[#0B0F0E] border border-white/10 text-white text-sm rounded
                      opacity-0 group-hover:opacity-100 transition-opacity duration-200
                      pointer-events-none whitespace-nowrap z-50
                    "
                    >
                      {item.label}
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </>
  );
};

export default SuperAdminSidebar;
